import { ASSESSMENT_STATUSES, DL_MODELS } from "../movenet/params";
import {
  startiOSVoiceListening,
  stopiOSVoiceListening,
} from "../utils/mobile-bridge";
import * as Sentry from "@sentry/browser";
import { config } from "../vision-algo/env_config";
import { SitStandAssessmentStates } from "./assessment_states";
import { SitStandAudioPlayer } from "./audio-player";
import { MoveNetPoseWrapper } from "./pose-wrapper";
import { SentryLogging } from "./sending_sentry_logs";
import { Position, SitStandProcessing } from "./sitStandProcessing";
import { GestureDetector } from "./start_gesture_detector";
import { MultiPoseUtility } from "./multipose_utility";
import { PatientSelection, find_person } from "./patient_selection";

// During the test, the patient may go undetected for this many frames in a row (~2/3 s) before it
// counts as someone crossing them: with two people close by, the patient's pose drops out for a
// frame or two, or the tracker gives them a new id
const MAX_FRAMES_WITHOUT_PATIENT = 20;

/**
 * The SitStand class orchestrates the 5x sit-to-stand assessment:
 * 1) Calibrates standing and sitting references via raised-hand gesture and detection.
 * 2) Monitors repeated sit-to-stand transitions.
 * 3) Terminates once five repetitions have been detected or upon errors.
 * 4) Sends results to the host environment (window.AssessmentPageReference).
 */
export class SitStand {
  /**
   * Constructs a new SitStand instance to manage the 5x sit-to-stand assessment.
   * @param {object} camera - A camera-like object with video feed & coordinate transformation.
   */
  constructor(camera) {
    this.camera = camera;
    this.current_state = SitStandAssessmentStates.begin_assessment;

    // Gesture detection and sit-stand logic
    this.gesture_detector = new GestureDetector();
    this.sit_stand_processing = new SitStandProcessing();

    // Timing controls
    this.start_time = null;
    this.end_time = null;
    this.first_frame = true;
    this.current_time = 0;
    this.first_frame_time = 0;
    this.start_timer_reference = 0;

    // ID management for patient/therapist
    this.therapist_id = -1;
    this.patient_id = -1;
    this.patient_ID_changed = false;
    // During the test: the patient's last pose and the tracking ids of the previous frame, to follow
    // a new tracking id (detect_patient_during_assessment)
    this.last_patient_pose = null;
    this.tracked_ids = null;

    // Activation status checks
    this.is_calibrated = false;
    this.frames_with_no_patient = 0;
    this.frames_out_of_box = 0;
    this.within_bound_start_flag = false;
    this.shown_persistent_icon = false;

    // Audio guidance
    this.audio_player = new SitStandAudioPlayer();

    // Display references
    this.rep_count_div = document.getElementById("div-sts-rep-count");
    this.insetoutputCanvas = document.getElementById("insetoutput");
    if (this.insetoutputCanvas) {
      this.insetoutputCanvas.style.display = "none";
    }

    // "Are you there" inactivity checks
    this.are_you_there_flag = true;
    this.are_you_there_timer = 0;
    this.inactive_for_60_seconds = false;
    this.are_you_there_modal_timer =
      config["are-you-there-timer-in-seconds"] * 1000;

    this.enabled_voice_listener = false;
    this.voice_command_received = false;
    // Set by receiveStopTrigger; freezes update() while the exit choice is shown
    this.stopped_by_trigger = false;

    window.addEventListener("voice-command", (event) => {
      const command = event.detail.command;
      console.log("🎤 [SitStand] Received voice command:", command);

      if (command === "START") {
        console.log("Voice Triggered START command");
        this.voice_command_received = true;
        this.enabled_voice_listener = false;
        stopiOSVoiceListening();
      }
    });

    // Sentry / Logging initialization
    let patientServerID = localStorage.getItem("patientServerID")
      ? localStorage.getItem("patientServerID")
      : "000";
    let userID = localStorage.getItem("UserID")
      ? localStorage.getItem("UserID")
      : "000";
    let businessCode = localStorage.getItem("BusinessCode")
      ? localStorage.getItem("BusinessCode")
      : "000";

    Sentry.setTag("UserID", userID);
    Sentry.setTag("Patient Server ID", patientServerID);
    Sentry.setTag("Business Code", businessCode);
    this.sentry_prefix = `5xSS_` + new Date().toISOString();
    ASSESSMENT_STATUSES.sentry_message = this.sentry_prefix;
    ASSESSMENT_STATUSES.are_you_there = false;
    ASSESSMENT_STATUSES.incomplete_test = false;
    ASSESSMENT_STATUSES.assessment_Completed = false;
    ASSESSMENT_STATUSES.assessment_successful_completion = false;

    this.sentry_start_time = 0;
    // Remote UP/DOWN patient selection: the participant the therapist selects before the start
    this.patient_selection = new PatientSelection();
  }

  /**
   * Main update function called on each frame. Manages the assessment state machine,
   * checks if participants remain in the bounding box, and triggers audio or UI events.
   * @param {Array} poses - Pose detection results for the current frame.
   */
  update(poses) {
    this.current_time = Date.now();

    // The participant the therapist selected with the remote, while it can still be changed.
    // patient_id also makes movenet draw this person's skeleton green.
    if (this.is_patient_selection_open()) {
      const patient = this.patient_selection.update(poses);
      this.patient_id = patient ? patient.id : -1;
    }

    // If the test is complete or in an error state, skip further processing
    if (
      this.current_state === SitStandAssessmentStates.test_complete ||
      ASSESSMENT_STATUSES.calibration_error_state ||
      this.current_state === SitStandAssessmentStates.assessment_interrupted ||
      this.stopped_by_trigger
    ) {
      // Stop voice listening on any termination condition
      if (this.enabled_voice_listener) {
        console.log(
          "🔊 Stopping voice listening due to assessment termination"
        );
        stopiOSVoiceListening();
        this.enabled_voice_listener = false;
      }
      if (ASSESSMENT_STATUSES.calibration_error_state) {
        this.sit_stand_processing.calibration_error_occured = true;
        this.sit_stand_processing.last_position = Position.Standing;
      }
      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
      return;
    }

    // "Are you there?" inactivity detection
    this.handleInactivity(poses);

    // Wait until DOM is loaded
    if (document.readyState !== "complete") {
      return;
    }

    // Handle first-frame logic: audio instructions and logging
    if (this.first_frame) {
      this.firstFrameLogic();
    }

    try {
      switch (this.current_state) {
        // --------------------------------- 1) BEGIN ASSESSMENT ----------------------------------
        case SitStandAssessmentStates.begin_assessment:
          // Draw bounding box around the patient area
          this.sit_stand_processing.draw_patient_box();

          // Check if participant is inside the bounding box
          if (
            // With a remote selection, only the selected person counts; otherwise anyone in the box
            this.are_within_bbox(
              this.patient_selection.start_check_poses(poses),
              this.sit_stand_processing.patient_box
            )
          ) {
            this.sit_stand_processing.draw_patient_box_green();
          } else {
            return;
          }

          // Delay until audio finishes or minimum wait passes
          if (
            !this.audio_player.is_available() ||
            this.current_time - this.first_frame_time <= 7000
          ) {
            return;
          }

          // Show a modal if newly within bounding box
          if (!this.within_bound_start_flag) {
            document.getElementById("hdnParticiantWithinBox").click();
            this.append_sentry_message(
              "Participant is within the Bounding box"
            );
            this.within_bound_start_flag = true;
            this.start_timer_reference = Date.now();
            // this.audio_player.play(SitStandAssessmentStates.with_in_bounds);
            return;
          }

          this.current_state = SitStandAssessmentStates.before_with_in_bounds;
          // document.getElementById("hdnSitStandRaiseHandMessage").click();
          this.append_sentry_message("Raise your hand to Calibrate.");
          this.within_bound_start_flag = false;
          this.start_timer_reference = Date.now();
          break;

        case SitStandAssessmentStates.before_with_in_bounds:
          if (
            // With a remote selection, only the selected person counts; otherwise anyone in the box
            this.are_within_bbox(
              this.patient_selection.start_check_poses(poses),
              this.sit_stand_processing.patient_box
            )
          ) {
            this.sit_stand_processing.draw_patient_box_green();
            this.frames_out_of_box = 0;

            if (
              this.current_time - this.start_timer_reference >= 3000
            ) {
              this.sit_stand_processing.draw_patient_box_green();

              this.audio_player.play(SitStandAssessmentStates.with_in_bounds);
              document.getElementById("hdnSitStandRaiseHandMessage").click();
              this.current_state = SitStandAssessmentStates.with_in_bounds;
            } else return;
          } else {
            this.sit_stand_processing.draw_patient_box();
            this.frames_out_of_box++;
            if (this.frames_out_of_box >= 30) {
              this.triggerOutOfBoxError();
              break;
            }
          }

          break;
        // --------------------------------- 2) WITHIN BOUNDS (AWAIT HAND RAISE) ------------------
        case SitStandAssessmentStates.with_in_bounds:
          if (
            // With a remote selection, only the selected person counts; otherwise anyone in the box
            this.are_within_bbox(
              this.patient_selection.start_check_poses(poses),
              this.sit_stand_processing.patient_box
            )
          ) {
            this.sit_stand_processing.draw_patient_box_green();
            this.frames_out_of_box = 0;

            // Detect hand raise every frame (not gated by audio)
            this.gesture_detector.detect_start_gesture(poses);

            // Voice listener gated by is_available() to avoid killing audio on iOS
            if (
              !this.enabled_voice_listener &&
              !this.voice_command_received &&
              this.audio_player.is_available()
            ) {
              console.log("🔊 Start of voice listening...");
              setTimeout(() => {
                startiOSVoiceListening();
              }, 100);
              this.enabled_voice_listener = true;
            }

            // Show persistent icon once audio finishes (replaces timeout-based approach)
            if (!this.shown_persistent_icon && this.audio_player.is_available()) {
              this.shown_persistent_icon = true;
              document.getElementById("hdnRaiseArmStopModalPopup")?.click();
            }

            if (
              this.gesture_detector.start_gesture_issued() ||
              this.voice_command_received
            ) {
              // Stop audio immediately if still playing
              if (!this.audio_player.is_available()) this.audio_player.stop();

              if (this.enabled_voice_listener) {
                console.log("🔊 Stopping voice listening...");
                stopiOSVoiceListening();
                this.enabled_voice_listener = false;
              }
              ASSESSMENT_STATUSES.sitstand_assessment_started = true;
              this.current_state =
                SitStandAssessmentStates.stand_gesture_issued;
              this.therapist_id = this.gesture_detector.gesture_issued_by;

              // Reset gesture detection for next usage
              this.gesture_detector.reset();
              document.getElementById("hdnHideHandRaisePersistentIcon")?.click();
              document.getElementById("hdnGaitCalibrationComplete").click();
              this.append_sentry_message("Calibration Completed.");
              this.append_sentry_message(
                "Number of persons in the frame while gesture detection: " +
                  poses.length.toString()
              );
            }
          } else {
            this.sit_stand_processing.draw_patient_box();
            if (this.enabled_voice_listener) {
              console.log("🔊 Stopping voice listening...");
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
            this.frames_out_of_box++;
            if (this.frames_out_of_box >= 30) {
              this.triggerOutOfBoxError();
              break;
            }
          }
          break;

        // --------------------------------- 3) STAND GESTURE ISSUED -----------------------------
        case SitStandAssessmentStates.stand_gesture_issued:
          if (this.checkAndHandleOutOfBox(poses)) break;
          // Check if participant is standing (head reference established)
          this.sit_stand_processing.is_standing(this.detect_patient(poses));
          if (this.sit_stand_processing.stand_head_detected()) {
            this.current_state = SitStandAssessmentStates.is_patient_sitting;
            this.audio_player.play(
              SitStandAssessmentStates.stand_gesture_issued
            );
            this.append_sentry_message(
              "Standing Height Reference is Captured."
            );
            document.getElementById("hdnClosePreviousModals").click();

            console.log("Standing Head Detection Complete");
            this.start_timer_reference = Date.now();

            // 30s fallback check if not sitting
            setTimeout(() => {
              if (
                this.current_state ===
                SitStandAssessmentStates.patient_seated_completely
              ) {
                console.log("Patient Sitting not detected for 30 Seconds");
                this.append_sentry_message(
                  "Patient Sitting not detected for 30 Seconds"
                );
              }
            }, 30000);
          }
          break;

        // --------------------------------- 4) PATIENT SITTING (TRANSITION) ----------------------
        case SitStandAssessmentStates.is_patient_sitting:
          if (this.checkAndHandleOutOfBox(poses)) break;
          // Wait 5s after stand reference, then check for sitting
          if (
            this.current_time - this.start_timer_reference >= 5000 &&
            this.sit_stand_processing.is_sitting(this.detect_patient(poses))
          ) {
            this.current_state =
              SitStandAssessmentStates.patient_seated_completely;
          }
          break;

        // --------------------------------- 5) PATIENT SEATED COMPLETELY ------------------------
        case SitStandAssessmentStates.patient_seated_completely:
          if (this.checkAndHandleOutOfBox(poses)) break;
          if (
            this.sit_stand_processing.is_fully_seated(
              this.detect_patient(poses)
            )
          ) {
            this.append_sentry_message(
              "Number of persons in the frame while Sitting detection: " +
                poses.length.toString()
            );
            this.append_sentry_message(
              "Sitting Height Reference is Captured., Sitting Detection time: " +
                (Date.now() - this.start_timer_reference) / 1000 +
                " seconds"
            );

            // Save the known sitting position
            this.sit_stand_processing.record_sitting_position(
              this.detect_patient(poses)
            );
            this.is_calibrated = true;
            this.current_state = SitStandAssessmentStates.about_to_start_test;

            // Audio for next step
            this.audio_player.play(
              SitStandAssessmentStates.patient_seated_completely
            );
            this.audio_player.start_assessment_audio.addEventListener(
              "ended",
              () => {
                document.getElementById("hdnCountDownCompletePopup").click();
                this.audio_player.play(
                  SitStandAssessmentStates.about_to_start_test
                );
              }
            );
          }
          break;

        // --------------------------------- 6) ABOUT TO START TEST ------------------------------
        case SitStandAssessmentStates.about_to_start_test:
          if (this.checkAndHandleOutOfBox(poses)) break;
          // If participant begins to stand up from the seated reference, start the timer
          if (
            this.sit_stand_processing.is_transitioning_to_stand(
              this.detect_patient(poses)
            )
          ) {
            this.current_state = SitStandAssessmentStates.begin_test;
            ASSESSMENT_STATUSES.sitstand_assessment_started = false;

            // Mark time references for the test
            this.sit_stand_processing.assessment_start_time = Date.now();
            this.sit_stand_processing.individual_rep_start_time_previous =
              this.sit_stand_processing.assessment_start_time;
            this.sit_stand_processing.assessment_end_time =
              this.sit_stand_processing.assessment_start_time;
            this.sit_stand_processing.last_position = Position.Sitting;

            this.append_sentry_message(
              "Sitting Reference: " +
                this.camera.scale_to_fit({
                  x: 0,
                  y: this.sit_stand_processing.sitting_reference_point,
                }).y +
                " Standing Reference: " +
                this.camera.scale_to_fit({
                  x: 0,
                  y: this.sit_stand_processing.standing_reference_point,
                }).y
            );
            this.append_sentry_message(
              "Feet Box Coordinates(x): " +
                this.sit_stand_processing.patient_box.x +
                ", " +
                (this.sit_stand_processing.patient_box.x +
                  this.sit_stand_processing.patient_box.width) +
                " Feet Box Coordinates(y): " +
                this.sit_stand_processing.patient_box.y +
                ", " +
                (this.sit_stand_processing.patient_box.y +
                  this.sit_stand_processing.patient_box.height)
            );
            this.append_sentry_message("Assessment Started");
          }
          this.frames_with_no_patient = 0;
          // Display remaining reps
          this.rep_count_div.innerText =
            5 - this.sit_stand_processing.sitting_count;
          break;

        // --------------------------------- 7) TEST IN PROGRESS ---------------------------------
        case SitStandAssessmentStates.begin_test:
          if (this.checkAndHandleOutOfBox(poses)) break;
          let pose = this.detect_patient_during_assessment(poses);

          // If a valid patient pose is found
          if (pose !== null && !this.patient_ID_changed) {
            this.frames_with_no_patient = 0;

            // Process sit-stand transitions
            this.sit_stand_processing.__start_5x(pose);
            this.rep_count_div.innerText =
              5 - this.sit_stand_processing.sitting_count;

            // Check if user triggered "save_incomplete_test"
            if (ASSESSMENT_STATUSES.save_incomplete_test) {
              ASSESSMENT_STATUSES.save_incomplete_test = false;

              // Mark times
              this.end_time = this.sit_stand_processing.assessment_end_time;
              this.start_time = this.sit_stand_processing.assessment_start_time;
              // Stop voice listening on successful completion
              if (this.enabled_voice_listener) {
                console.log(
                  "🔊 Stopping voice listening due to assessment completion"
                );
                stopiOSVoiceListening();
                this.enabled_voice_listener = false;
              }
              this.current_state = SitStandAssessmentStates.test_complete;
              this.append_sentry_message("Assessment Completed!");
              ASSESSMENT_STATUSES.assessment_Completed = true;
              ASSESSMENT_STATUSES.assessment_successful_completion = true;

              this.completion_function();

              let response = this.build_response();
              console.log("exiting the assessment in middle: ", response);

              this.append_sentry_message(
                "Assessment response:" + JSON.stringify(response)
              );

              if (window["AssessmentPageReference"]) {
                window["AssessmentPageReference"].SaveAssessment(response);
              } else {
                console.log("window['AssessmentPageReference'] not found");
              }
            } else {
              // If 5 reps are completed
              if (
                this.sit_stand_processing.sitting_count >= 5 &&
                this.sit_stand_processing.standing_count >= 5
              ) {
                this.end_time = this.sit_stand_processing.assessment_end_time;
                this.start_time =
                  this.sit_stand_processing.assessment_start_time;
                // Stop voice listening on completion
                if (this.enabled_voice_listener) {
                  console.log(
                    "🔊 Stopping voice listening due to assessment completion"
                  );
                  stopiOSVoiceListening();
                  this.enabled_voice_listener = false;
                }
                this.current_state = SitStandAssessmentStates.test_complete;

                // Announce completion, show UI
                this.audio_player.play(SitStandAssessmentStates.test_complete);
                document.getElementById("hdnCompletionTickPopup").click();
                this.append_sentry_message("Assessment Completed!");
                ASSESSMENT_STATUSES.assessment_Completed = true;
                ASSESSMENT_STATUSES.assessment_successful_completion = true;

                this.completion_function();

                let response = this.build_response();
                console.log(response);
                this.append_sentry_message(
                  "Assessment response:" + JSON.stringify(response)
                );

                if (window["AssessmentPageReference"]) {
                  window["AssessmentPageReference"].CompleteAssessment(
                    response
                  );
                } else {
                  console.log("window['AssessmentPageReference'] not found");
                }
              }
            }
          } else {
            // Patient not found: skip this frame, and error out only once they've been missing a while
            this.frames_with_no_patient++;
            console.log("frames_with_no_patient:", this.frames_with_no_patient);
            if (this.frames_with_no_patient < MAX_FRAMES_WITHOUT_PATIENT) break;

            // Stop voice listening when patient leaves frame
            if (this.enabled_voice_listener) {
              console.log(
                "🔊 Stopping voice listening due to patient leaving frame"
              );
              stopiOSVoiceListening();
              this.enabled_voice_listener = false;
            }
            this.current_state = SitStandAssessmentStates.test_complete;
            document.getElementById("hdnAssessementErrorPopup").click();
            ASSESSMENT_STATUSES.assessment_Completed = true;
            localStorage.removeItem("modalDisplayed");
            this.audio_player.play(
              SitStandAssessmentStates.assessment_interrupted
            );

            this.append_sentry_message(
              "Assessment ERROR occured - Someone has crossed the Patient during assessment"
            );
            this.frames_with_no_patient = 0;
          }
          break;

        default:
          break;
      }

      // Mark lines for references if calibration is done
      if (this.is_calibrated) {
        this.sit_stand_processing.mark_lines_onCanvas(
          this.camera.scale_to_fit({
            x: 0,
            y: this.sit_stand_processing.standing_reference_point,
          })
        );
        this.sit_stand_processing.mark_lines_onCanvas(
          this.camera.scale_to_fit({
            x: 0,
            y: this.sit_stand_processing.sitting_reference_point,
          })
        );
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * True while the patient can be changed with the remote: from the first frame (the patient box is
   * fixed) until the start is accepted.
   * @returns {boolean}
   */
  is_patient_selection_open() {
    return (
      [
        SitStandAssessmentStates.begin_assessment,
        SitStandAssessmentStates.before_with_in_bounds,
        SitStandAssessmentStates.with_in_bounds,
      ].includes(this.current_state) &&
      !this.voice_command_received &&
      !this.stopped_by_trigger &&
      !ASSESSMENT_STATUSES.exit_assessment_modal &&
      !ASSESSMENT_STATUSES.calibration_error_state
    );
  }

  /**
   * Patient selection from outside the frame loop (BLE remote UP/DOWN): moves the patient to the
   * next detected person on screen. They still have to be in the patient box for the test to start.
   * @param {number} direction - +1 for the next person to the right, -1 for the next to the left.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the selection changed.
   */
  cyclePatient(direction, source) {
    if (!this.is_patient_selection_open()) return false;

    const selected = this.patient_selection.cycle(direction);
    if (!selected) return false;

    this.patient_id = selected.id;
    this.append_sentry_message(`Patient selected by ${source}: pose id ${selected.id}`);
    return true;
  }

  /**
   * Start trigger from outside the frame loop (BLE remote START_STOP). Accepted once waiting for the
   * start with the selected participant in the box, also while the raise-hand prompt is still
   * playing (it is cut short).
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStartTrigger(source) {
    // A remote start needs a participant selected with UP/DOWN (a hand raise or voice start doesn't)
    const patient = this.patient_selection.selected_patient();
    if (
      !patient ||
      this.current_state !== SitStandAssessmentStates.with_in_bounds ||
      this.voice_command_received ||
      !MultiPoseUtility.are_both_ankles_in_canvas_box(
        [patient],
        this.sit_stand_processing.patient_box,
        this.camera,
        0.2
      )
    ) {
      return false;
    }
    // Same flag as voice START, so update() starts the test through the existing path
    this.voice_command_received = true;
    // Cut the prompt short: the remote may start while it is still playing
    this.audio_player.stop();
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }
    this.append_sentry_message(`Start triggered by ${source}`);
    return true;
  }

  /**
   * Stop trigger from outside the frame loop (BLE remote START_STOP). Accepted only while the
   * test is running (start triggered, not yet finished or errored). Freezes update() so nothing
   * more is measured while the caller shows the exit choice.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {boolean} True if the trigger was accepted.
   */
  receiveStopTrigger(source) {
    const running = [
      SitStandAssessmentStates.stand_gesture_issued,
      SitStandAssessmentStates.is_patient_sitting,
      SitStandAssessmentStates.patient_seated_completely,
      SitStandAssessmentStates.about_to_start_test,
      SitStandAssessmentStates.begin_test,
    ].includes(this.current_state);
    if (
      !running ||
      this.stopped_by_trigger ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return false;
    }
    // Own flag rather than exit_assessment_modal, so the header Exit keeps its current behaviour
    this.stopped_by_trigger = true;
    this.append_sentry_message(`Assessment stopped by ${source}`);
    return true;
  }

  /**
   * NEXT trigger from outside the frame loop (BLE remote NEXT). Sit-to-Stand has a single phase, so
   * before the test starts NEXT leaves the assessment. Freezes update() while the caller navigates away.
   * @param {string} source - Where the trigger came from, for the Sentry log.
   * @returns {"exit"|null} What the caller should do, or null if NEXT doesn't apply now.
   */
  receiveNextTrigger(source) {
    const before_start = [
      SitStandAssessmentStates.begin_assessment,
      SitStandAssessmentStates.before_with_in_bounds,
      SitStandAssessmentStates.with_in_bounds,
    ].includes(this.current_state);
    if (
      !before_start ||
      this.voice_command_received ||
      this.stopped_by_trigger ||
      ASSESSMENT_STATUSES.calibration_error_state
    ) {
      return null;
    }
    this.stopped_by_trigger = true;
    this.append_sentry_message(`Assessment exited by ${source} (NEXT before start)`);
    return "exit";
  }

  /**
   * Handles inactivity by showing the "Are You There?" modal if no poses are detected for too long.
   * @param {Array} poses - The detected poses in the current frame.
   */
  handleInactivity(poses) {
    if (!poses || poses.length === 0) {
      if (this.are_you_there_flag) {
        this.are_you_there_flag = false;
        this.are_you_there_timer = this.current_time;
      }
      const time_since_screen_inactive =
        this.current_time - this.are_you_there_timer;
      if (
        time_since_screen_inactive >= 60000 &&
        !this.inactive_for_60_seconds
      ) {
        this.inactive_for_60_seconds = true;
        this.append_sentry_message(
          "No pose/activity detected for more than 60 seconds."
        );
      }
      if (time_since_screen_inactive >= this.are_you_there_modal_timer) {
        this.are_you_there_flag = true;
        this.are_you_there_timer = 0;
        document.getElementById("hdnAreYouTherePopup").click();
      }
    } else {
      this.are_you_there_flag = true;
      this.are_you_there_timer = 0;
    }
  }

  /**
   * Logic that runs only in the first frame after the constructor.
   * Plays an initial audio prompt, logs model info, and sets up Sentry messages.
   */
  firstFrameLogic() {
    this.first_frame = false;

    // Start voice instructions
    this.audio_player.play(SitStandAssessmentStates.begin_assessment);
    document.getElementById("hdnAssessmentStartUpMessage").click();

    // Flip camera horizontally for user convenience
    this.camera.flip_horizontal();

    // If there's a leftover message about deep-learning model load time, log it
    if (DL_MODELS.MessageForSentry === "") {
      this.append_sentry_message("DL_Model already loaded");
    } else {
      this.append_sentry_message(DL_MODELS.MessageForSentry);
      DL_MODELS.MessageForSentry = "";
    }

    // Log resolution and show initial instructions
    this.append_sentry_message(
      "Screen Resolution: " +
        this.sit_stand_processing.canvas.width +
        "x" +
        this.sit_stand_processing.canvas.height
    );
    this.append_sentry_message(
      "'Place chair around 8 feet away from phone' modal is displayed"
    );
    this.first_frame_time = this.current_time;
  }

  /**
   * Draws the bounding box and checks if the patient is out of the box.
   * Uses a consecutive frame counter to avoid false errors during sit/stand transitions.
   * Returns true if the error was triggered (caller should return/break).
   * @param {Array} poses - The detected poses in the current frame.
   * @returns {boolean} True if an out-of-box error was triggered.
   */
  checkAndHandleOutOfBox(poses) {
    const inBox = this.are_within_bbox(
      poses,
      this.sit_stand_processing.patient_box
    );

    if (inBox) {
      this.sit_stand_processing.draw_patient_box_green();
      this.frames_out_of_box = 0;
      return false;
    }

    // Patient is out of box — draw brown box and increment counter
    this.sit_stand_processing.draw_patient_box();
    this.frames_out_of_box++;

    // ~60 consecutive frames (~2 seconds) out of box triggers error
    if (this.frames_out_of_box >= 30) {
      this.triggerOutOfBoxError();
      return true;
    }
    return false;
  }

  /**
   * Triggers the assessment error when the patient has left the bounding box.
   * Follows the established pattern: set error flag FIRST, then UI updates.
   */
  triggerOutOfBoxError() {
    // Set error flag FIRST to prevent race conditions with timeouts
    ASSESSMENT_STATUSES.assessment_error = true;
    ASSESSMENT_STATUSES.assessment_Completed = true;

    this.current_state = SitStandAssessmentStates.test_complete;

    // Stop voice listening
    if (this.enabled_voice_listener) {
      stopiOSVoiceListening();
      this.enabled_voice_listener = false;
    }

    // Stop any playing audio
    this.audio_player.stop();

    // Show error modal
    document.getElementById("hdnAssessementErrorPopup").click();

    // Play assessment interrupted audio
    this.audio_player.play(SitStandAssessmentStates.assessment_interrupted);

    localStorage.removeItem("modalDisplayed");
    this.frames_out_of_box = 0;

    this.append_sentry_message(
      "Assessment ERROR - Patient moved out of bounding box"
    );
  }

  /**
   * Resets the assessment back to the before_with_in_bounds state for retry.
   * Called from the error modal's Retry button via hdnRetryCurrentPhase.
   */
  retryAssessment() {
    // Reset state to begin_assessment so patient has time to get back in box
    // without triggering out-of-box error (begin_assessment has no error counter)
    this.current_state = SitStandAssessmentStates.begin_assessment;

    // Reset calibration
    this.is_calibrated = false;
    this.patient_id = -1;
    this.therapist_id = -1;
    this.last_patient_pose = null;
    this.tracked_ids = null;
    this.patient_selection.reset();
    this.patient_ID_changed = false;
    this.frames_with_no_patient = 0;
    this.frames_out_of_box = 0;
    this.shown_persistent_icon = false;
    this.voice_command_received = false;
    this.within_bound_start_flag = false;

    // Reset gesture detector
    this.gesture_detector.reset();

    // Reset sit-stand processing calibration data
    this.sit_stand_processing.__stand_head_detected = false;
    this.sit_stand_processing.standing_reference_point = null;
    this.sit_stand_processing.sitting_reference_point = null;
    this.sit_stand_processing.sitting_count = 0;
    this.sit_stand_processing.standing_count = 0;
    this.sit_stand_processing.last_position = Position.Standing;
    this.sit_stand_processing.patient_femur_2_tibia_ratio = 1000;
    this.sit_stand_processing.patient_in_sitting_region = true;
    this.sit_stand_processing.patient_stood_up = false;
    this.sit_stand_processing.calibration_error_occured = false;

    // Reset assessment statuses
    ASSESSMENT_STATUSES.assessment_Completed = false;
    ASSESSMENT_STATUSES.assessment_error = false;
    ASSESSMENT_STATUSES.assessment_successful_completion = false;
    ASSESSMENT_STATUSES.sitstand_assessment_started = false;

    this.start_timer_reference = Date.now();
    this.first_frame_time = Date.now();

    // Play setup audio and show startup modal directly (camera is already flipped)
    this.audio_player.play(SitStandAssessmentStates.begin_assessment);
    document.getElementById("hdnAssessmentStartUpMessage").click();

    this.append_sentry_message("Assessment RETRY - returning to begin_assessment");
  }

  /**
   * Checks whether any person's ankles are within a specified bounding box (foot box).
   * @param {Array} poses - The array of pose objects from the pose detector.
   * @param {object} bounding_box - An object { x, y, width, height } for the foot box.
   * @returns {boolean} True if a person is within the box, false otherwise.
   */
  are_within_bbox(poses, bounding_box) {
    let result = MultiPoseUtility.are_both_ankles_in_canvas_box(
      poses,
      bounding_box,
      this.camera,
      0.2
    );
    this.sit_stand_processing.is_patient_in_box_status = result;
    return result;
  }

  /**
   * Builds the final response object with total times and repetitions to be
   * passed to the external environment upon assessment completion.
   * @returns {object} The data object summarizing all reps and overall timing.
   */
  build_response() {
    let mobility = this.calculate_mobility();

    // Ensure no undefined reps
    for (let i = 0; i < 5; i++) {
      if (
        this.sit_stand_processing.individual_rep[i] === "undefined" ||
        this.sit_stand_processing.individual_rep[i] == null
      ) {
        this.sit_stand_processing.individual_rep[i] = 0;
        mobility = "Low";
      }
    }

    // Sum total times
    this.sit_stand_processing.total_time = 0;
    for (let i in this.sit_stand_processing.individual_rep) {
      this.sit_stand_processing.total_time +=
        this.sit_stand_processing.individual_rep[i];
    }

    return {
      test_type: "five_x_sit_stand",
      skipped: false,
      mobility: mobility,
      repetitions: {
        repetition: [
          { index: 0, time: this.sit_stand_processing.individual_rep[0] },
          { index: 1, time: this.sit_stand_processing.individual_rep[1] },
          { index: 2, time: this.sit_stand_processing.individual_rep[2] },
          { index: 3, time: this.sit_stand_processing.individual_rep[3] },
          { index: 4, time: this.sit_stand_processing.individual_rep[4] },
        ],
      },
      overall: {
        time: this.sit_stand_processing.total_time,
      },
      start_time: this.sit_stand_processing.assessment_start_time,
      end_time: this.sit_stand_processing.assessment_end_time,
    };
  }

  /**
   * Determines an overall "mobility" rating based on the total time taken for the 5 reps.
   * Currently uses fixed cutoffs: <8.1s=High, <15s=Medium, else=Low.
   * @returns {string} "High", "Medium", or "Low".
   */
  calculate_mobility() {
    let overall_time =
      this.sit_stand_processing.assessment_end_time -
      this.sit_stand_processing.assessment_start_time;

    if (overall_time < 8100) return "High";
    if (overall_time < 15000) return "Medium";
    return "Low";
  }

  /**
   * Identifies a single patient among multiple poses by looking for a known ID
   * or by picking the person inside the bounding box. Records the chosen ID as needed.
   * @param {Array} poses - Array of multi-person pose objects.
   * @returns {MoveNetPoseWrapper|null} A wrapper around the identified patient pose or null if none found.
   */
  detect_patient(poses) {
    if (this.patient_id !== -1) {
      for (let person of poses) {
        if (person.score > 0.25 && person.id === this.patient_id) {
          this.patient_ID_changed = false;
          return new MoveNetPoseWrapper(person);
        }
      }
    }
    if (!poses || poses.length === 0) {
      return null;
    }

    // Identify the best candidate inside the bounding box
    let therapist_pose = null;
    let min_feet_to_center_distance = 10000;
    let patient_ID = null;

    for (let person of poses) {
      if (person.id === this.therapist_id) {
        therapist_pose = person;
      }
      if (person.score > 0.25 && person.id !== this.therapist_id) {
        let left_ankle = this.camera.scale_to_fit({
          x: person.keypoints[15].x,
          y: person.keypoints[15].y,
        });
        let right_ankle = this.camera.scale_to_fit({
          x: person.keypoints[16].x,
          y: person.keypoints[16].y,
        });

        // Check bounding box constraints
        if (
          right_ankle.x > this.sit_stand_processing.patient_box.x &&
          left_ankle.y > this.sit_stand_processing.patient_box.y &&
          left_ankle.x <
            this.sit_stand_processing.patient_box.x +
              this.sit_stand_processing.patient_box.width &&
          right_ankle.y <
            this.sit_stand_processing.patient_box.y +
              this.sit_stand_processing.patient_box.height
        ) {
          let current_feet_to_center_distance = Math.abs(
            (left_ankle.x + right_ankle.x) / 2 -
              (this.sit_stand_processing.patient_box.x +
                (this.sit_stand_processing.patient_box.x +
                  this.sit_stand_processing.patient_box.width)) /
                2
          );

          if (current_feet_to_center_distance < min_feet_to_center_distance) {
            min_feet_to_center_distance = current_feet_to_center_distance;
            patient_ID = person;
            this.patient_id = person.id;
          }
        }
      }
    }

    // Return the non-therapist if found
    if (patient_ID) {
      this.patient_ID_changed = true;
      return new MoveNetPoseWrapper(patient_ID);
    }

    // Otherwise, check if the therapist is inside the box
    if (therapist_pose) {
      let left_ankle = this.camera.scale_to_fit({
        x: therapist_pose.keypoints[15].x,
        y: therapist_pose.keypoints[15].y,
      });
      let right_ankle = this.camera.scale_to_fit({
        x: therapist_pose.keypoints[16].x,
        y: therapist_pose.keypoints[16].y,
      });

      if (
        right_ankle.x > this.sit_stand_processing.patient_box.x &&
        left_ankle.y > this.sit_stand_processing.patient_box.y &&
        left_ankle.x <
          this.sit_stand_processing.patient_box.x +
            this.sit_stand_processing.patient_box.width &&
        right_ankle.y <
          this.sit_stand_processing.patient_box.y +
            this.sit_stand_processing.patient_box.height
      ) {
        this.patient_ID_changed = true;
        patient_ID = therapist_pose;
        this.patient_id = therapist_pose.id;
        return new MoveNetPoseWrapper(patient_ID);
      }
    }
    return null;
  }

  /**
   * The patient while the test is in progress: the pose with their tracking id or, if the tracker
   * gave them a new id, the newly tracked pose nearest to where they were (never someone already
   * tracked as someone else). Unlike detect_patient it doesn't pick someone else from the box: a
   * different person would be "someone crossing the patient".
   * @param {Array} poses - Current frame's detected poses.
   * @returns {MoveNetPoseWrapper|null} The patient, or null if not found (or not confidently
   *   detected) in this frame.
   */
  detect_patient_during_assessment(poses) {
    const person = find_person(
      poses,
      this.patient_id,
      this.last_patient_pose,
      this.tracked_ids
    );
    this.tracked_ids = new Set((poses || []).filter(Boolean).map((pose) => pose.id));
    if (!person) return null;

    this.patient_id = person.id; // follows a new tracking id
    this.last_patient_pose = person;
    this.patient_ID_changed = false;
    // Too unsure a pose to count repetitions from this frame (e.g. overlapping someone)
    if (person.score <= 0.25) return null;
    return new MoveNetPoseWrapper(person);
  }

  /**
   * Called upon test completion to console-log debug information
   * about each repetition's detection times and positions.
   */
  completion_function() {
    console.log("Test Completed");
    console.log(
      "1st Sit Position =",
      this.sit_stand_processing.sitting_position[1],
      "1st Sit Time =",
      this.sit_stand_processing.individual_rep[0]
    );
    console.log(
      "2nd Sit Position =",
      this.sit_stand_processing.sitting_position[2],
      "2nd Sit Time =",
      this.sit_stand_processing.individual_rep[1]
    );
    console.log(
      "3rd Sit Position =",
      this.sit_stand_processing.sitting_position[3],
      "3rd Sit Time =",
      this.sit_stand_processing.individual_rep[2]
    );
    console.log(
      "4th Sit Position =",
      this.sit_stand_processing.sitting_position[4],
      "4th Sit Time =",
      this.sit_stand_processing.individual_rep[3]
    );
    console.log(
      "5th Sit Position =",
      this.sit_stand_processing.sitting_position[5],
      "5th Sit Time =",
      this.sit_stand_processing.individual_rep[4]
    );
  }

  /**
   * Appends a custom message string to the global Sentry log, including a timestamp.
   * Also includes time-based triggers for specific log messages (e.g., 30s timeouts).
   * @param {string} message - The text to add to the Sentry log.
   */
  append_sentry_message(message) {
    let event = new Date();
    ASSESSMENT_STATUSES.sentry_message +=
      "\n" + event.toISOString() + ": " + message;

    // For calibration step timing
    if (message === "Raise your hand to Calibrate.") {
      this.sentry_start_time = Date.now();
      setTimeout(() => {
        if (this.current_state === SitStandAssessmentStates.with_in_bounds) {
          console.log("Gesture (Raise Hand) not detected for 30 Seconds \n");
          ASSESSMENT_STATUSES.sentry_message +=
            "\n Gesture (Raise Hand) not detected for 30 Seconds";
        }
      }, 30000);
    } else if (message === "Calibration Completed.") {
      let diff = (Date.now() - this.sentry_start_time) / 1000;
      ASSESSMENT_STATUSES.sentry_message +=
        ",  Raise Hand Calibration time: " + diff + " seconds";
    }
  }
}

/**
 * Initializes Sentry logging specifically for 5x sit-stand assessments.
 */
export async function sitStandSentryLogging() {
  SentryLogging("5xSS");
}

export default SitStand;
